import { useEffect, useRef, useState } from "react";
import { ImagePlus, Plus, X } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IS_API_MODE } from "../../shared/config/runtime";
import {
  createPost,
  createPostDraft,
  getPostDraft,
  publishPostDraft,
  updatePostDraft,
  uploadPostMedia,
} from "../../entities/post/api";
import { getApiErrorMessage } from "../../shared/api/errors";
import { initialPosts } from "../../shared/data/mockData";
import { showToast } from "../../shared/ui/toast";

const designCategories = [
  { value: "POSTER", label: "海报设计" },
  { value: "BRAND_VISUAL", label: "品牌视觉" },
  { value: "PROMOTION", label: "宣传图" },
  { value: "TYPOGRAPHY", label: "字体设计" },
  { value: "PACKAGING", label: "包装设计" },
];

export default function CreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedDraftId = searchParams.get("draft");
  const mediaFilesRef = useRef([]);
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState(designCategories[0].value);
  const [tags, setTags] = useState("");
  const [mediaFiles, setMediaFiles] = useState([]);
  const [activeMediaId, setActiveMediaId] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [loadingDraft, setLoadingDraft] = useState(Boolean(requestedDraftId));
  const [draftLoadError, setDraftLoadError] = useState("");
  const [publishedPostId, setPublishedPostId] = useState(null);
  const [publishError, setPublishError] = useState("");

  const activeMedia =
    mediaFiles.find((item) => item.id === activeMediaId) || mediaFiles[0] || null;
  const isDraftMode = Boolean(requestedDraftId || draftId);
  const isWaitingForRequestedDraft =
    Boolean(requestedDraftId)
    && String(draftId || "") !== String(requestedDraftId)
    && !draftLoadError;

  useEffect(() => {
    mediaFilesRef.current = mediaFiles;
  }, [mediaFiles]);

  useEffect(
    () => () => {
      mediaFilesRef.current.forEach((item) => {
        if (item.ownsPreviewUrl) URL.revokeObjectURL(item.previewUrl);
      });
    },
    [],
  );

  useEffect(() => {
    if (!IS_API_MODE || !requestedDraftId) {
      setLoadingDraft(false);
      return undefined;
    }
    let disposed = false;
    setLoadingDraft(true);
    setDraftLoadError("");
    getPostDraft(requestedDraftId)
      .then((draft) => {
        if (disposed) return;
        const draftMedia = [...(draft.media_items || [])]
          .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))
          .map((item, index) => ({
            id: `draft-media-${item.id || item.temp_resource_id}`,
            file: null,
            name:
              item.extra_json?.original_filename
              || item.file_key?.split("/").pop()
              || `草稿图片 ${index + 1}`,
            size: Number(item.file_size || 0),
            previewUrl: item.file_url || item.fileUrl || "",
            ownsPreviewUrl: false,
            uploaded: {
              temp_resource_id: item.temp_resource_id,
              file_size: Number(item.file_size || 0),
            },
          }));
        const draftTags = (draft.tags || [])
          .map((item) => item.name || item)
          .filter(Boolean);
        setDraftId(String(draft.id));
        setTitle(draft.title || "");
        setCaption(draft.content || "");
        setCategory(
          designCategories.some((item) => item.value === draft.post_type)
            ? draft.post_type
            : designCategories[0].value,
        );
        setTags(draftTags.join(", "));
        setMediaFiles(draftMedia);
        setActiveMediaId(draftMedia[0]?.id || "");
      })
      .catch((error) => {
        if (!disposed) setDraftLoadError(getApiErrorMessage(error, "草稿加载失败"));
      })
      .finally(() => {
        if (!disposed) setLoadingDraft(false);
      });
    return () => {
      disposed = true;
    };
  }, [requestedDraftId]);

  function getMediaName(media) {
    return media.file?.name || media.name || "作品图片";
  }

  function getMediaSize(media) {
    return Number(media.file?.size || media.size || media.uploaded?.file_size || 0);
  }

  function chooseMedia(event) {
    const selectedFiles = Array.from(event.target.files || []);
    if (!selectedFiles.length) return;

    const existingKeys = new Set(
      mediaFiles.map((item) =>
        item.file
          ? `${item.file.name}:${item.file.size}:${item.file.lastModified}`
          : `remote:${item.uploaded?.temp_resource_id}`,
      ),
    );
    const additions = selectedFiles
      .filter((file) => file.type.startsWith("image/"))
      .filter((file) => {
        const key = `${file.name}:${file.size}:${file.lastModified}`;
        if (existingKeys.has(key)) return false;
        existingKeys.add(key);
        return true;
      })
      .map((file) => ({
        id: crypto.randomUUID(),
        file,
        name: file.name,
        size: file.size,
        previewUrl: URL.createObjectURL(file),
        ownsPreviewUrl: true,
      }));

    if (additions.length) {
      setMediaFiles((current) => [...current, ...additions]);
      if (!mediaFiles.length) setActiveMediaId(additions[0].id);
    }
    event.target.value = "";
  }

  function removeMedia(mediaId) {
    const removedIndex = mediaFiles.findIndex((item) => item.id === mediaId);
    if (removedIndex < 0) return;
    const removed = mediaFiles[removedIndex];
    const remaining = mediaFiles.filter((item) => item.id !== mediaId);
    if (removed.ownsPreviewUrl) URL.revokeObjectURL(removed.previewUrl);
    setMediaFiles(remaining);
    if (activeMediaId === mediaId) {
      setActiveMediaId(remaining[removedIndex]?.id || remaining[removedIndex - 1]?.id || "");
    }
  }

  function getSelectedCategory() {
    return designCategories.find((item) => item.value === category) || designCategories[0];
  }

  function getTagNames() {
    return tags
      .split(/[,，#\s]+/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  async function ensureMediaUploaded() {
    if (!mediaFiles.length) {
      setUploadProgress(100);
      return [];
    }
    const uploadedMedia = [];
    for (let index = 0; index < mediaFiles.length; index += 1) {
      const media = mediaFiles[index];
      if (media.uploaded) {
        uploadedMedia.push(media.uploaded);
        setUploadProgress(Math.round(((index + 1) / mediaFiles.length) * 100));
        continue;
      }
      if (!media.file) throw new Error("草稿图片资源缺少本地文件");
      const uploaded = await uploadPostMedia(media.file, (fileProgress) => {
        const overallProgress = Math.round(
          ((index + fileProgress / 100) / mediaFiles.length) * 100,
        );
        setUploadProgress(overallProgress);
      });
      uploadedMedia.push(uploaded);
      setMediaFiles((current) =>
        current.map((item) => (item.id === media.id ? { ...item, uploaded } : item)),
      );
    }
    return uploadedMedia;
  }

  function buildPostPayload(uploadedMedia) {
    const selectedCategory = getSelectedCategory();
    return {
      post_type: selectedCategory.value,
      title: title.trim(),
      content: caption.trim(),
      detail_json: {
        design_category: selectedCategory.label,
        original_filename: mediaFiles[0] ? getMediaName(mediaFiles[0]) : "",
        original_filenames: mediaFiles.map(getMediaName),
      },
      tag_names: getTagNames(),
      topic_names: [selectedCategory.label],
      media_inputs: uploadedMedia.map((uploaded, index) => ({
        temp_resource_id: uploaded.temp_resource_id,
        media_type: "image",
        is_cover: index === 0,
        file_size: uploaded.file_size,
        sort_order: index,
      })),
    };
  }

  function buildDraftPayload(uploadedMedia) {
    const postPayload = buildPostPayload(uploadedMedia);
    return {
      post_type: postPayload.post_type,
      title: postPayload.title,
      content: postPayload.content,
      detail_json: postPayload.detail_json,
      tag_names: postPayload.tag_names,
      topic_names: postPayload.topic_names,
      media_inputs: postPayload.media_inputs.map(({ file_size: _fileSize, ...item }) => item),
    };
  }

  async function saveDraft() {
    if (publishing || savingDraft) return;
    const hasDraftContent =
      Boolean(title.trim() || caption.trim() || tags.trim() || mediaFiles.length);
    if (!hasDraftContent) {
      showToast("请先填写内容或选择图片", "error");
      return;
    }

    setSavingDraft(true);
    setPublishError("");
    setUploadProgress(0);
    try {
      if (!IS_API_MODE) {
        setDraftId("demo-draft");
        showToast("草稿保存成功");
        return;
      }
      const uploadedMedia = await ensureMediaUploaded();
      const payload = buildDraftPayload(uploadedMedia);
      const saved = draftId
        ? await updatePostDraft(draftId, payload)
        : await createPostDraft(payload);
      setDraftId(String(saved.id || draftId));
      showToast(draftId ? "草稿已更新" : "草稿保存成功");
    } catch (error) {
      const message = getApiErrorMessage(error, "草稿保存失败");
      setPublishError(message);
      showToast(message, "error");
    } finally {
      setSavingDraft(false);
    }
  }

  async function submitPost(event) {
    event.preventDefault();
    if (!title.trim() || !mediaFiles.length || publishing || savingDraft) return;

    setPublishing(true);
    setPublishError("");
    setUploadProgress(0);
    try {
      if (!IS_API_MODE) {
        setUploadProgress(100);
        setPublishedPostId(initialPosts[0].id);
        return;
      }

      const uploadedMedia = await ensureMediaUploaded();
      if (draftId) {
        await updatePostDraft(draftId, buildDraftPayload(uploadedMedia));
        const published = await publishPostDraft(draftId);
        setPublishedPostId(String(published.post_id || published.postId));
      } else {
        const created = await createPost(buildPostPayload(uploadedMedia));
        setPublishedPostId(String(created.id));
      }
      showToast("作品发布成功");
    } catch (error) {
      setPublishError(getApiErrorMessage(error, "作品发布失败"));
      showToast(getApiErrorMessage(error, "作品发布失败"), "error");
    } finally {
      setPublishing(false);
    }
  }

  return (
    <section className="page-container narrow-page create-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">{isDraftMode ? "草稿编辑" : "作品发布"}</span>
          <h1>{isDraftMode ? "继续完善作品" : "打造个人名片"}</h1>
        </div>
      </div>
      <form className="composer-card" onSubmit={submitPost}>
        {loadingDraft || isWaitingForRequestedDraft ? (
          <div className="detail-state">正在加载草稿……</div>
        ) : draftLoadError ? (
          <div className="detail-state error">{draftLoadError}</div>
        ) : publishedPostId ? (
          <div className="success-state">
            <span>✓</span>
            <h2>发布成功</h2>
            <p>你的设计作品已经发布到图π作品流。</p>
            <button
              type="button"
              className="primary-button"
              onClick={() => navigate(`/posts/${publishedPostId}`)}
            >
              查看作品
            </button>
          </div>
        ) : (
          <>
            {activeMedia ? (
              <div className="composer-media-picker">
                <div className="composer-media-preview">
                  <img src={activeMedia.previewUrl} alt={`待发布图片 ${mediaFiles.indexOf(activeMedia) + 1}`} />
                  {mediaFiles[0]?.id === activeMedia.id ? <span>封面</span> : null}
                  <button type="button" onClick={() => removeMedia(activeMedia.id)} aria-label="删除当前图片">
                    <X size={18} />
                  </button>
                </div>
                <div className="composer-media-strip" aria-label="已选择的图片">
                  {mediaFiles.map((media, index) => (
                    <button
                      key={media.id}
                      className={media.id === activeMedia.id ? "active" : ""}
                      type="button"
                      onClick={() => setActiveMediaId(media.id)}
                      aria-label={`预览第 ${index + 1} 张图片${index === 0 ? "，封面" : ""}`}
                    >
                      <img src={media.previewUrl} alt="" />
                      <span>{index + 1}</span>
                    </button>
                  ))}
                  <label className="composer-media-add">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      multiple
                      onChange={chooseMedia}
                    />
                    <Plus size={22} />
                    <span>添加</span>
                  </label>
                </div>
              </div>
            ) : (
              <label className="upload-zone">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={chooseMedia}
                />
                <>
                  <span className="upload-illustration">
                    <ImagePlus size={38} />
                  </span>
                  <strong>选择设计稿或展示图</strong>
                  <small>支持同时选择多张 JPG、PNG、WebP，首张图片将作为封面</small>
                  <span className="secondary-button">从电脑选择图片</span>
                </>
              </label>
            )}
            <label className="field-label" htmlFor="post-title">
              作品标题
            </label>
            <input
              id="post-title"
              className="composer-input"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="例如：春日茶饮品牌上新海报"
              maxLength={255}
            />
            <div className="composer-row">
              <label>
                <span>设计分类</span>
                <select value={category} onChange={(event) => setCategory(event.target.value)}>
                  {designCategories.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>标签</span>
                <input
                  value={tags}
                  onChange={(event) => setTags(event.target.value)}
                  placeholder="品牌视觉, 字体排版"
                />
              </label>
            </div>
            <label className="field-label" htmlFor="caption">
              作品说明（选填）
            </label>
            <textarea
              id="caption"
              value={caption}
              onChange={(event) => setCaption(event.target.value)}
              placeholder="介绍设计背景、创意策略、字体与配色思路……"
              maxLength={1000}
            />
            <div className="composer-meta">
              <span>
                {mediaFiles.length
                  ? `${mediaFiles.length} 张图片 · ${(mediaFiles.reduce((total, item) => total + getMediaSize(item), 0) / 1024 / 1024).toFixed(2)} MB`
                  : "请选择作品图片"}
              </span>
              <span>{caption.length}/1000</span>
            </div>
            {(publishing || savingDraft) && (
              <div className="upload-progress" aria-label={`上传进度 ${uploadProgress}%`}>
                <span style={{ width: `${uploadProgress}%` }} />
                <b>
                  {uploadProgress < 100
                    ? `上传 ${uploadProgress}%`
                    : savingDraft
                      ? "正在保存草稿……"
                      : "正在发布作品……"}
                </b>
              </div>
            )}
            {publishError && <div className="form-error">{publishError}</div>}
            <div className="composer-footer">
              <button className="ghost-button" type="button" onClick={() => navigate("/")}>
                取消
              </button>
              <button
                className="secondary-button"
                type="button"
                disabled={publishing || savingDraft}
                onClick={saveDraft}
              >
                {savingDraft ? "保存中……" : draftId ? "更新草稿" : "暂存草稿"}
              </button>
              <button
                className="primary-button"
                type="submit"
                disabled={!title.trim() || !mediaFiles.length || publishing || savingDraft}
              >
                {publishing ? "发布中……" : "发布作品"}
              </button>
            </div>
          </>
        )}
      </form>
    </section>
  );
}
